import Vue from "vue";
import deepmerge from "deepmerge";
import set from "lodash/set";
import { get, calcularEdad } from "@/utils";
import * as HELPERS from "@/pixels/utils/helpers";
import {
  nombresPaquetes,
  nombresTarjetasPorCodigo
} from "@/config/constants/packagesCodes";
import { nombreTarjetaDebito } from "@/config/constants/paquetesBeneficios";

const isProduction = PRODUCTION;

const AdobeFunctions = {
  internal(context, _callback) {
    AdobeFunctions.setConstants();
    AdobeFunctions.setAge(context);
    AdobeFunctions.setGender(context);
    AdobeFunctions.setUserVars(context);
    AdobeFunctions.setOptimizations(context);
    AdobeFunctions.setOperationNumber(
      get(context, "state.dataOferta.nroDeTramite", "")
    );
    setTimeout(_callback, 0);
  },
  callAdobeDTM(method, event) {
    /**
     * Realizar llamada al DTM
     */
    if (event !== "Page View" && event !== "Internal Link") {
      // No se debe reportar los props de application para page views
      AdobeFunctions.setApplicationProps();
    }
    try {
      window[method](event);
    } catch (error) {
      Vue.$log.error("🚀 Adobe Launch error: ", error);
    }
    Vue.$log.info("🚀 Adobe Launch call: ", event);
  },
  clearVars() {
    /**
     * Limpiar las variables cuando hace falta
     */
    set(window.digitalData, "products.attributes", []);
    set(window.digitalData, "internalCampaign.attributes", []);
    set(window.digitalData, "user.segment.profile", "");
    set(window.digitalData, "page.pageInfo.level2", "");
    set(window.digitalData, "page.pageInfo.pageName", "");
    set(window.digitalData, "page.pageInfo.errorPage", "");
    set(window.digitalData, "page.pageActivity.link.name", "");
    set(window.digitalData, "application.step", "");
    set(window.digitalData, "application.state", "");
    set(window.digitalData, "application.typology", "");
    set(window.digitalData, "application.errorType", "");
    set(window.digitalData, "application.globalApplication", "");
    set(window.digitalData, "application.isQualifiedVisits", "");
    set(
      window.digitalData,
      "application",
      deepmerge(window.digitalData.application, {
        application: {
          type: "",
          name: ""
        },
        fulfillmentModel: ""
      })
    );
  },
  setPageInstanceId() {
    /**
     * Establecer entorno en datalayer
     */
    set(window.digitalData, "pageInstanceID", isProduction ? "pro" : "de");
  },
  setIsMobile() {
    if (HELPERS._isMobile()) {
      set(window.digitalData, "user.device.mobile", "si");
    } else {
      set(window.digitalData, "user.device.mobile", "no");
    }
  },
  setSysEnv() {
    if (HELPERS._isMobile()) {
      set(window.digitalData, "page.pageInfo.sysEnv", "web-movil");
    } else {
      set(window.digitalData, "page.pageInfo.sysEnv", "escritorio");
    }
  },
  setPageName(pageName) {
    let segments = [
      get(window.digitalData, "page.pageInfo.sysEnv", "escritorio"),
      "publica",
      "general",
      HELPERS.getApplicationName()
    ];
    if (pageName) {
      segments.push(pageName);
      set(window.digitalData, "page.pageInfo.level2", pageName);
    }
    set(window.digitalData, "page.pageInfo.pageName", segments.join(":"));
  },
  setConstants() {
    set(window.digitalData, "page.pageInfo.server", window.location.hostname);
    set(window.digitalData, "user.device.userAgent", navigator.userAgent);
    AdobeFunctions.setPageInstanceId();
    AdobeFunctions.setIsMobile();
    AdobeFunctions.setSysEnv();
  },
  setOptimizations(context) {
    if (context && context.state && context.state.adobeTargetExperience) {
      /**
       * Se especificó una experiencia de Target. Indicarla al tagging.
       * La experiencia DEBE venir de Target con el formato: `IDACTIVIDAD_EXPERIENCIA`
       * Ej: "AR072019_B"
       */
      let datosTarget = context.state.adobeTargetExperience.split("_");
      if (datosTarget.length === 2) {
        let attributes = [
          {
            idOptimization: datosTarget[0],
            experience: datosTarget[1],
            place: "",
            type: "",
            executor: "Target",
            audience: ""
          }
        ];
        set(window.digitalData, "optimization.attributes", attributes);
      }
    }
  },
  setInternalCampaign() {
    let attributes = [
      {
        location: "thank you page-account form-centro-1-1",
        campaignFormat: "card",
        collectiveCode: "VD-",
        campaignName: "",
        product: "",
        productCode: "",
        quantity: ""
      }
    ];
    set(window.digitalData, "internalCampaign.attributes", attributes);
  },
  setProductAttributes(oferta) {
    let attributes = [];
    let attributesModel = {
      primaryCategory: "",
      productSubtype: "",
      productName: "",
      productCode: "",
      quantity: "",
      amount: "",
      paymentAmount: "",
      numberOfPayments: "",
      paymentDate: "",
      paymentType: "",
      serviceCharge: "",
      currency: "",
      numberOfHolders: "",
      interestRate: {
        tin: "",
        tae: "",
        rate: ""
      },
      term: "",
      group: "",
      state: ""
    };

    let paquete = get(oferta, "paquetes[0]", null);
    let customProductAttributes = get(oferta, "productAttributes", null);

    if (paquete) {
      // Cajas de ahorro
      get(paquete, "cajasDeAhorro", []).forEach(cuenta => {
        attributes.push(
          deepmerge(attributesModel, {
            primaryCategory: "cuentas",
            productSubtype: "caja de ahorro",
            productName: HELPERS._normalizeCharacters(
              get(cuenta, "descripcion", "")
            ),
            currency: HELPERS._normalizeCurrency(get(cuenta, "moneda", "")),
            group: HELPERS._normalizeCharacters(
              nombresPaquetes(paquete.tipoPaquete)
            )
          })
        );
      });

      // Cuentas corrientes
      get(paquete, "cuentasCorrientes", []).forEach(caja => {
        attributes.push(
          deepmerge(attributesModel, {
            primaryCategory: "cuentas",
            productSubtype: "cuenta corriente",
            productName: "cuenta corriente pesos",
            amount: get(caja, "limite", "").toString(),
            currency: HELPERS._normalizeCurrency(get(caja, "moneda", "")),
            group: HELPERS._normalizeCharacters(
              nombresPaquetes(paquete.tipoPaquete)
            )
          })
        );
      });

      // Tarjetas de crédito
      get(paquete, "tarjetas", []).forEach(tarjeta => {
        attributes.push(
          deepmerge(attributesModel, {
            primaryCategory: "tarjetas",
            productSubtype: "tarjetas credito",
            productName: HELPERS._normalizeCharacters(
              nombresTarjetasPorCodigo(tarjeta.tipoTarjeta)
            ),
            amount: get(tarjeta, "limite", "").toString(),
            currency: HELPERS._normalizeCurrency(get(tarjeta, "moneda", "ARS")),
            group: HELPERS._normalizeCharacters(
              nombresPaquetes(paquete.tipoPaquete)
            )
          })
        );
      });

      // Tarjetas de débito
      attributes.push(
        deepmerge(attributesModel, {
          primaryCategory: "tarjetas",
          productSubtype: "tarjetas debito",
          productName: HELPERS._normalizeCharacters(
            nombreTarjetaDebito(paquete.tipoPaquete)
          ),
          currency: "ARS",
          group: HELPERS._normalizeCharacters(
            nombresPaquetes(paquete.tipoPaquete)
          )
        })
      );
    }

    if (customProductAttributes) {
      customProductAttributes.forEach(attr => {
        attributes.push(deepmerge(attributesModel, attr));
      });
    }

    set(window.digitalData, "products.attributes", attributes);
  },
  setCommonProductAttributes() {
    AdobeFunctions.setProductAttributes({
      productAttributes: [
        {
          primaryCategory: "cuentas",
          productSubtype: "caja de ahorro",
          productName: "caja de ahorro pesos"
        },
        {
          primaryCategory: "tarjetas",
          productSubtype: "tarjetas debito",
          productName: "tarjeta de debito tradicional"
        }
      ]
    });
  },
  setProductState(state) {
    get(window.digitalData, "products.attributes", []).forEach(
      (product, index) => {
        set(window.digitalData.products.attributes, `[${index}].state`, state);
      }
    );
  },
  setApplicationProps() {
    set(
      window.digitalData,
      "application",
      deepmerge(window.digitalData.application, {
        application: {
          type: "contratacion",
          name: HELPERS.getApplicationName()
        },
        fulfillmentModel: HELPERS.getFulfillmentModel(),
        globalApplication: HELPERS.getGlobalApplication()
      })
    );
  },
  setOperationNumber(operationNumber) {
    set(window.digitalData, "application.operationNumber", operationNumber);
  },
  setUserVars(context) {
    set(
      window.digitalData,
      "user.prospectID",
      get(context, "state.userIdTokenizado", "")
    );
  },
  setGender(context) {
    set(
      window.digitalData,
      "user.gender",
      HELPERS._normalizeGender(get(context, "state.consulta.sexo", ""))
    );
  },
  setAge(context) {
    const edad = calcularEdad(
      get(context, "state.consulta.fechaNacimiento", "")
    );
    set(window.digitalData, "user.age", edad);
  },
  setTypology() {
    set(window.digitalData, "application.typology", HELPERS.getTypology());
  },
  setOffer(context) {
    set(window.digitalData, "application.offer", HELPERS.getOffer(context));
  }
};

export { AdobeFunctions };
